import isEqual from "lodash/isEqual"

export const anyWidgetsRendered = (children) => {
  // An empty dashboard returns an array of falses, e.g. [false, false]
  // so if one value is truthy, we probably have something displaying
  return children && children.length > 0 && children.some(x => !!x)
}

/** Detect whether anything has changed in the filters
 * which should trigger a reload of data in dashboards
 */
export const filtersHaveMeaningfulChange = (currentFilters, nextFilters) => {
  // 1: First check whether all filters have completed loading
  const getLoadingArray = (filters) => {
    return [
      filters.fields.appraisalLevels.isLoading,
      filters.fields.generations.isLoading,
      filters.fields.genders.isLoading,
      filters.fields.jobTitles.isLoading,
      filters.fields.locations.isLoading,
      filters.fields.review.isLoading,
      filters.fields.viewMode.isLoading,
    ]
  }

  const nextLoadingStates = getLoadingArray(nextFilters)

  // If none of the next filters are loading,
  // check whether the current previous had
  // at least one filter still loading
  if (nextLoadingStates.indexOf(true) === -1) {
    const currentLoadingStates = getLoadingArray(currentFilters)

    if (currentLoadingStates.indexOf(true) >= 0) {
      return true
    }
  }

  // 2: Next check whether any of the values have changed
  const getValuesArray = (filters) => {
    return [
      filters.fields.appraisalLevels.selectedValue,
      filters.fields.dataMode.selectedValue,
      filters.fields.endDate.selectedValue,
      filters.fields.generations.selectedValue,
      filters.fields.genders.selectedValue,
      filters.fields.jobTitles.selectedValue,
      filters.fields.locations.selectedValue,
      filters.fields.review.selectedValue,
      filters.fields.startDate.selectedValue,
      filters.fields.viewMode.selectedValue,
      filters.fields.managers.selectedValue,
    ]
  }

  const currentValues = getValuesArray(currentFilters)
  const nextValues = getValuesArray(nextFilters)

  // Ignore a change from null to a value for viewMode,
  // that shouldn't trigger dashboards loading, as Step 1 above
  // covers that same scenario by handling when all filters
  // have loaded
  if (
    currentFilters.fields.viewMode.selectedValue !== null &&
    !isEqual(currentValues, nextValues)
  ) {
    return true
  }

  return false
}

/** Checks whether the data for the versus line chart widget has the
 * data needed to display the widget
 */
export const performanceComparisonChartHasData = (data) => {
  // If there aren't any dropdown options to compare, return false
  if (!data || !data.comparisonSelectOptions || data.comparisonSelectOptions.length === 0) {
    return false
  }

  // If there's no value arrays at all, return false
  if (!data.locationDrilldownSummary || !data.generationDrilldownSummary) return false

  // Check if there are values in the series
  if (
    data.generationDrilldownSummary.rootSeries.length > 0 &&
    data.locationDrilldownSummary.rootSeries.length > 0
  ) {
    let hasData = false
    for (let index = 0; index < data.generationDrilldownSummary.rootSeries.length; index++) {
      const element = data.generationDrilldownSummary.rootSeries[index]

      if (element.drilldownObjects.length > 0 || element.nonDrilldownObjects.length > 0) {
        hasData = true
      }
    }

    for (let index = 0; index < data.locationDrilldownSummary.rootSeries.length; index++) {
      const element = data.locationDrilldownSummary.rootSeries[index]

      if (element.drilldownObjects.length > 0 || element.nonDrilldownObjects.length > 0) {
        hasData = true
      }
    }
    return hasData
  }

  // If there are no values in the series return false
  return false
}

/** Checks whether the data for the nps widget has the
 * data needed to display the widget
 */
export const customNpsHasData = (data) => {
  // If there aren't any options, return false
  if (!data || !data.currentReviewYearSummary || !data.currentReviewYearSummary.scores) {
    return false
  }

  return true
}

export const npsHasData = (data) => {
  // If there aren't any options, return false
  if (!data || !data.currentReviewYearSummary) {
    return false
  }

  return true
}

export const completionHasData = (data) => {
  if (!data) return false
  if (data.totalReviewsSent < 1) return false
  if (!data.rootSeries || data.rootSeries.length < 1) return false
  if (
    (!data.rootSeries[0].drilldownObjects && !data.rootSeries[0].nonDrilldownObjects) ||
    (data.rootSeries[0].drilldownObjects.length < 1 &&
      data.rootSeries[0].nonDrilldownObjects.length < 1)
  ) {
    return false
  }

  return true
}

export const top5LdHasData = (data) => {
  if (!data || !data.rootSeries || data.rootSeries.length < 1 || !data.rootSeries[0]) {
    return false
  }

  if (!data.rootSeries[0].drilldownObjects && !data.rootSeries[0].nonDrilldownObjects) return false

  if (
    data.rootSeries[0].drilldownObjects.length < 1 &&
    data.rootSeries[0].nonDrilldownObjects.length < 1
  ) {
    return false
  }

  return true
}

export const tempCheckScoresHaveResults = (data) => {
  if (!data) return false

  return data.some(element => element.results && element.results.length > 0)
}

export const keyItemDataHasData = (data) => {
  if (data == null || !data || data.length < 1) return false

  return true
}

export const engagementHasData = (data) => {
  if (!data || !data.averages || data.averages.length < 1) return false

  if (!data.overTime || data.overTime.length < 1) return false

  return true
}

export const employeeCountsHasData = (data) => {
  if (!data) return false

  return data.some(element => element.totalEmployees > 0)
}

export const lAndDCompletedHasData = (data) => {
  if (!data) return false

  return data[0].totalEmployees > 0
}

export const happyHasData = (data) => {
  if (!data || !data.dataSets || data.dataSets.length < 1) return false

  return data.dataSets.some(element => element.data && element.data.length > 0)
}
export const engagementAveragesHasData = (data) => {
  if (data == null || !data || data.length < 1) return false

  return data[0].value > 0
}

export const arrayOfItemsWithCountsHasData = (data) => {
  if (!data || data.length < 1) return false

  return data.some(element => element.count > 0)
}

export const arrayValuesHaveData = (data) => {
  if (!data || data.length < 1) return false

  return data.some(element => element.value > 0)
}

export const tempCheckResponseHasData = (data) => {
  if (!data || data.value < 1) return false

  return true
}

export const nineBoxGridHasData = (data) => {
  if (data && data.reviews && data.reviews.length > 0) return true

  return false
}

export const pulseChecksHasData = (data) => {
  if (data && data.questions && data.questions.length > 0) {
    if (data.summaries && data.summaries.length > 0) {
      return data.summaries.some(summary => summary && summary.answerSummaries && summary.answerSummaries.length > 0)
    }
  }
  return false
}

export const journeysHasData = (data) => {
  if (data && data.groupedQuestions && data.groupedQuestions.length > 0) {
    if (data.summaries && data.summaries.length > 0) {
      return data.summaries.some(summary =>
        summary &&
          summary.answerSummaries &&
          summary.answerSummaries.some(answer => answer.answerCount > 0) > 0)
    }
  }
  return false
}

export const arrayOfItemNameValuePairsHasData = (data) => {
  if (data && data.length > 0) {
    return data.some(d => d && d.value > 0)
  }

  return false
}



// WEBPACK FOOTER //
// ./src/utils/object-utils.js